import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * SearchableSelect — a Combobox built on shadcn's Command + Popover.
 *
 * Use this in place of the plain <Select> whenever the option list is
 * long enough to need typing-to-search (clients, employees, etc.).
 *
 * Props:
 *   value        — current selected value (string)
 *   onValueChange(v) — fires with the new value (or "" when cleared)
 *   options      — [{ value, label }]
 *   placeholder  — shown when nothing selected
 *   emptyLabel   — option for "no selection" (e.g. "No client")
 *   size         — "sm" | "md" (default "sm")
 *   testId       — base data-testid; option items get `${testId}-option-${value}`
 */
export default function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select…",
  emptyLabel = null,
  size = "sm",
  testId = "searchable-select",
  className = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  const display = selected ? selected.label : (emptyLabel || placeholder);

  const heightCls = size === "md" ? "h-9" : "h-7";
  const textCls = size === "md" ? "text-sm" : "text-xs";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={`${heightCls} ${textCls} font-normal justify-between ${className}`}
        >
          <span className="truncate text-left">{display}</span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64 z-[80]" align="start">
        <Command>
          <CommandInput placeholder="Type to search…" data-testid={`${testId}-search`}/>
          <CommandList>
            <CommandEmpty>No matches.</CommandEmpty>
            <CommandGroup>
              {emptyLabel && (
                <CommandItem
                  value="__none__"
                  onSelect={() => { onValueChange(""); setOpen(false); }}
                  data-testid={`${testId}-option-none`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${!value ? "opacity-100" : "opacity-0"}`}/>
                  <span className="text-slate-500 italic">{emptyLabel}</span>
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.value}`}
                  onSelect={() => { onValueChange(o.value); setOpen(false); }}
                  data-testid={`${testId}-option-${o.value}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === o.value ? "opacity-100" : "opacity-0"}`}/>
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
